import { TicketType, type TicketState } from '@/api/constant/ticket';
import {
  type ApiRequest,
  type ApiResponse,
  callApi,
  deleteAPI
} from '@/api/tenantClient';
import { type TicketDiscount } from './ticketDiscount';

export const ticketQueryKey = 'registration.ticket';
export const functionTicketListQueryKey = 'registration.functionTicketList';

export interface TicketData {
  id: number
  eventId: string
  state: TicketState
  type: TicketType
  name: string
  description: string
  price: number
  priceFormat: string
  stock: number
  createdAt: string | null
  updatedAt: string | null
  code: string
  validFrom: string
  validTo: string
  attendeeCategories: Array<{
    id: number
    name: string
  }>
  ticketDiscounts?: TicketDiscount[]
  discountValue: number
  discountValueFormat: string
  currentStock: number
  displayTaxInclusivePrices: {
    isEnabled: boolean
    taxName: string
  }
  ticketSharedStockId: number | null
  hideRemainingStock: boolean
  hideRemainingTime: boolean
}

export interface TicketSaveRequestItem extends Omit<TicketData, 'id' | 'createdAt' | 'updatedAt' | 'state' | 'type' | 'attendeeCategories' | 'eventId' | 'currentStock'> {
  id?: number
  eventIds: string[]
  attendeeCategoryIds: string[]
}

export interface TicketSaveRequest extends TicketSaveRequestItem {
  ticketType: TicketType
}

export function saveTicket(createTenantAdminRequest: () => ApiRequest) {
  const request = createTenantAdminRequest();
  return async (params: TicketSaveRequest): Promise<ApiResponse<TicketData>> => {
    const path = params.ticketType === TicketType.Main ? '/api/v1/delegate_types' : '/api/v1/sessions_pricings';
    const payload = params.ticketType === TicketType.Main ? { delegateType: params } : { sessionsPricing: params };
    if (params.id) {
      request.method = 'PUT';
      request.endpoint.path = path + `/${params.id}`;
      request.endpoint.query = {
        eventId: params.eventIds[0]
      };
      request.payload = payload;
    } else {
      request.method = 'POST';
      request.endpoint.path = path;
      request.endpoint.query = {
        eventId: params.eventIds[0]
      };
      request.payload = payload;
    }
    return await callApi<TicketData>(request);
  };
}

export interface TicketDeleteRequest {
  id: string
  ticketType: TicketType
}

export function deleteTicket(createTenantAdminRequest: () => ApiRequest) {
  const request = createTenantAdminRequest();
  return async (params: TicketDeleteRequest) => {
    const path = params.ticketType === TicketType.Main ? '/api/v1/delegate_types/' : '/api/v1/sessions_pricings/';
    request.method = 'DELETE';
    request.endpoint.path = path + params.id;
    await deleteAPI(request);
  };
}

export const getProductType = (type: TicketType) => {
  if (type === TicketType.Main) {
    return 'DelegateType';
  } else {
    return 'SessionsPricing';
  }
};

export interface TicketCloneRequest {
  id: string
  ticketType: TicketType
}

export function cloneTicket(createTenantAdminRequest: () => ApiRequest) {
  const request = createTenantAdminRequest();
  return async (params: TicketCloneRequest): Promise<ApiResponse<TicketData>> => {
    const path = params.ticketType === TicketType.Main ? '/api/v1/delegate_types/clone' : '/api/v1/sessions_pricings/clone';
    request.method = 'POST';
    request.endpoint.path = path;
    request.endpoint.query = { id: params.id.toString() };
    return await callApi<TicketData>(request);
  };
}
